import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Lock, CheckCircle2, CalendarClock, Sparkles, ArrowRight } from "lucide-react";
import { api, track } from "@/lib/api";
import CheckinFlow from "@/components/CheckinFlow";

const FEATURE_ROUTE = { coach: "/coach", journal: "/journal", affirmations: "/affirmations", community: "/community", learn: "/learn", tools: "/tools" };

const LEVEL = { 1: "Very low", 2: "Low", 3: "Okay", 4: "Good", 5: "Great" };
const MONTH_RATE = ["Really hard", "Hard", "Okay", "Good", "Wonderful"];
const MOODS = [
  { v: "great", label: "Great" }, { v: "good", label: "Good" }, { v: "okay", label: "Okay" },
  { v: "low", label: "Low" }, { v: "rough", label: "Rough" },
];

// Day-scoped phrasing so daily questions feel present-moment.
const DAILY_Q = {
  stress: "How manageable does your stress feel today?",
  energy: "How's your energy today?",
  sleep: "How well did you sleep last night?",
  confidence: "How confident do you feel in yourself today?",
  connection: "How connected did you feel to others today?",
  emotional: "How balanced do your emotions feel today?",
};

const fmtDate = (iso) => { const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); };

export default function Wellness() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("daily");
  const [dims, setDims] = useState([]);
  const [trends, setTrends] = useState([]);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [assessHist, setAssessHist] = useState([]);
  const [assessStatus, setAssessStatus] = useState(null);
  const [savingD, setSavingD] = useState(false);
  const [savingM, setSavingM] = useState(false);
  const [dailyKey, setDailyKey] = useState(0);
  const [redoDaily, setRedoDaily] = useState(false);
  const [recs, setRecs] = useState(null);

  const loadTrends = () => api.get("/wellness/trends").then(({ data }) => setTrends(data.checkins || []));
  const loadStatus = () => api.get("/wellness/status").then(({ data }) => setStatus(data));
  const loadHistory = () => api.get("/wellness/monthly").then(({ data }) => setHistory(data || []));

  useEffect(() => {
    track("wellness_viewed");
    api.get("/wellness/dimensions").then(({ data }) => setDims(data));
    loadTrends(); loadStatus(); loadHistory();
    api.get("/assessment/history").then(({ data }) => setAssessHist(data || [])).catch(() => {});
    api.get("/assessment/status").then(({ data }) => setAssessStatus(data)).catch(() => {});
  }, []);

  const dailySteps = [
    { id: "mood", kicker: "Right now", kind: "choice", question: "How are you feeling right now?", options: MOODS },
    ...dims.map((d) => ({ id: d.id, kicker: d.label, kind: "choice", question: DAILY_Q[d.id] || `How is your ${d.label.toLowerCase()} today?`, options: [1, 2, 3, 4, 5].map((n) => ({ v: n, label: LEVEL[n] })) })),
    { id: "note", kicker: "Optional", kind: "text", question: "Anything about today you want to note?" },
  ];

  const saveDaily = async (a) => {
    setSavingD(true);
    try {
      const scores = Object.fromEntries(dims.map((d) => [d.id, a[d.id] || 3]));
      const { data } = await api.post("/wellness/checkin", { scores, mood: a.mood, note: a.note || "" });
      track("wellness_checkin_created");
      setRecs(data?.recommendations || null);
      toast.success("Daily check-in saved. Notice the pattern, not the number.");
      setRedoDaily(false); setDailyKey((k) => k + 1);
      loadTrends(); loadStatus();
    } finally { setSavingD(false); }
  };

  const monthlySteps = [
    { id: "overall", kicker: "This month", kind: "choice", question: "Looking back, how was this whole month?", options: [1, 2, 3, 4, 5].map((n) => ({ v: n, label: MONTH_RATE[n - 1] })) },
    { id: "grew", kicker: "The month behind you", kind: "text", question: "Where did you grow this month?" },
    { id: "drained", kicker: "The month behind you", kind: "text", question: "What drained you most this month?" },
    { id: "win", kicker: "The month behind you", kind: "text", question: "Name one win from this month — big or small." },
    { id: "intention", kicker: "The month ahead", kind: "text", question: "What's your intention for next month?" },
  ];

  const saveMonthly = async (a) => {
    setSavingM(true);
    try {
      await api.post("/wellness/monthly", { overall: a.overall || 3, grew: a.grew || "", drained: a.drained || "", win: a.win || "", intention: a.intention || "" });
      toast.success("Monthly reflection saved. Beautiful work, sister.");
      loadStatus(); loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Please try again.");
    } finally { setSavingM(false); }
  };

  const chartData = trends.map((c) => ({ date: new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }), ...c.scores }));
  const COLORS = { stress: "#B97978", energy: "#C89B55", sleep: "#7B628B", confidence: "#5BA37D", connection: "#3D6B52", emotional: "#D4A359" };
  const tabCls = (t) => `px-5 py-2.5 rounded-full text-sm font-medium transition-smooth ${tab === t ? "bg-[#291B2D] text-[#FCFAF6]" : "text-[#291B2D]/60 hover:bg-[#F7F2EA]"}`;

  return (
    <div className="space-y-8" data-testid="wellness-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Wellness</p>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-1">How are you, really?</h1>
        <p className="text-[#59535B] mt-2 max-w-xl">A gentle check-in — not a diagnosis. Come daily; reflect monthly.</p>
      </div>

      <div className="inline-flex items-center gap-1 bg-[#F1EAE0] rounded-full p-1" data-testid="wellness-tabs">
        <button data-testid="wellness-tab-daily" className={tabCls("daily")} onClick={() => setTab("daily")}>Daily</button>
        <button data-testid="wellness-tab-monthly" className={tabCls("monthly")} onClick={() => setTab("monthly")}>Monthly</button>
      </div>

      {tab === "daily" && (
        <div className="space-y-6">
          {recs && (
            <div className="bg-[#291B2D] text-[#FCFAF6] rounded-3xl p-7 lg:p-8 max-w-2xl card-soft" data-testid="checkin-recommendations">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">For you, right now</p>
              <h2 className="font-display text-2xl mt-1">{recs.headline}</h2>
              <p className="text-[#FCFAF6]/70 mt-2 text-sm">A couple of gentle next steps, tailored to today's check-in.</p>
              <div className="mt-5 space-y-3">
                {recs.items.map((it, i) => (
                  <button key={i} data-testid={`rec-item-${it.feature}`}
                    onClick={() => navigate(FEATURE_ROUTE[it.feature] || "/home")}
                    className="w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-4 transition-smooth group">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 h-8 w-8 rounded-full bg-[#C89B55]/20 flex items-center justify-center shrink-0">
                        <Sparkles size={15} className="text-[#C89B55]" />
                      </span>
                      <div className="flex-1">
                        <p className="font-medium">{it.title}</p>
                        <p className="text-[#FCFAF6]/70 text-sm mt-0.5">{it.body}</p>
                        <span className="inline-flex items-center gap-1 text-[#C89B55] text-sm font-medium mt-2 group-hover:gap-2 transition-all">
                          {it.cta} <ArrowRight size={14} />
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <button data-testid="rec-dismiss" onClick={() => setRecs(null)}
                className="mt-5 text-sm text-[#FCFAF6]/50 hover:text-[#FCFAF6]/80 transition-smooth">Maybe later</button>
            </div>
          )}
          {status?.daily_done_today && !redoDaily ? (
            <div className="bg-white border border-[#E5DED4] rounded-3xl p-8 max-w-2xl card-soft" data-testid="daily-done">
              <span className="inline-flex items-center gap-2 text-[#5BA37D] font-medium"><CheckCircle2 size={18} /> You've checked in today</span>
              <p className="text-[#59535B] mt-2">Beautifully done. Come back tomorrow — or check in again if things have shifted.</p>
              <button data-testid="daily-again" onClick={() => { setRedoDaily(true); setDailyKey((k) => k + 1); }}
                className="mt-5 rounded-full border border-[#E5DED4] hover:bg-[#F7F2EA] px-6 py-3 text-sm font-medium">Check in again</button>
            </div>
          ) : (
            dims.length > 0 && <CheckinFlow key={`d-${dailyKey}`} steps={dailySteps} onComplete={saveDaily} submitting={savingD} ctaLabel="Save daily check-in" flowTestId="daily-flow" />
          )}

          {chartData.length >= 2 && (
            <div className="bg-[#F7F2EA] border border-[#E5DED4] rounded-2xl p-6 lg:p-8 max-w-2xl" data-testid="wellness-trends">
              <h2 className="font-display text-xl font-semibold mb-4">Your trends</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5DED4" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#8A838E" }} />
                    <YAxis domain={[1, 5]} tick={{ fontSize: 12, fill: "#8A838E" }} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5DED4" }} />
                    {dims.map((d) => <Line key={d.id} type="monotone" dataKey={d.id} name={d.label} stroke={COLORS[d.id]} strokeWidth={2} dot={false} />)}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "monthly" && (
        <div className="space-y-6">
          <div className="bg-white border border-[#E5DED4] rounded-2xl p-6 lg:p-8 max-w-2xl" data-testid="wellness-score-history">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display text-xl font-semibold">Your Wellness Score</h2>
                <p className="text-sm text-[#59535B]">From your monthly wellness check-in.</p>
              </div>
              {assessStatus && (assessStatus.can_retake ? (
                <button data-testid="assessment-retake" onClick={() => { sessionStorage.removeItem("sh_last_result"); window.location.assign("/assessment"); }}
                  className="rounded-full bg-[#291B2D] text-white px-4 py-2 text-sm font-medium hover:bg-[#352238] transition-smooth">Retake</button>
              ) : <span className="text-xs text-[#8A838E]" data-testid="assessment-retake-locked">Retake {fmtDate(assessStatus.next_retake)}</span>)}
            </div>
            {assessHist.length === 0 ? <p className="text-sm text-[#59535B]">Take the wellness check-in to start tracking your score here.</p>
              : assessHist.length === 1 ? <p className="font-display text-5xl">{assessHist[0].wellness_score}<span className="text-lg text-[#8A838E]">/100</span></p>
              : (
                <div className="h-56" data-testid="wellness-score-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={assessHist.map((h) => ({ date: new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }), score: h.wellness_score }))} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5DED4" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#8A838E" }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#8A838E" }} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5DED4" }} />
                      <Line type="monotone" dataKey="score" name="Wellness" stroke="#C89B55" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
          </div>

          {status && status.monthly_available ? (
            <CheckinFlow steps={monthlySteps} onComplete={saveMonthly} submitting={savingM} ctaLabel="Save monthly reflection" flowTestId="monthly-flow" />
          ) : (
            <div className="bg-[#291B2D] text-[#FCFAF6] rounded-2xl p-8 max-w-2xl text-center" data-testid="monthly-locked">
              <div className="mx-auto h-14 w-14 rounded-full bg-white/10 flex items-center justify-center"><Lock size={22} className="text-[#C89B55]" /></div>
              <h2 className="font-display text-2xl mt-4">Your monthly reflection is resting</h2>
              <p className="text-[#FCFAF6]/70 mt-2 inline-flex items-center justify-center gap-2"><CalendarClock size={16} /> Reopens {status ? fmtDate(status.monthly_next_unlock) : "soon"}</p>
              <p className="text-[#FCFAF6]/60 text-sm mt-3 max-w-md mx-auto">You've already reflected this month. Keep showing up for your daily check-ins in the meantime.</p>
            </div>
          )}

          {history.length > 0 && (
            <div className="max-w-2xl space-y-3" data-testid="monthly-history">
              <h3 className="font-display text-lg">Look back</h3>
              {history.map((h, i) => (
                <div key={i} className="bg-white border border-[#E5DED4] rounded-2xl p-5">
                  <span className="text-sm font-medium">{fmtDate(h.created_at)}</span>
                  {h.intention && <p className="text-sm text-[#59535B] mt-2"><span className="font-medium text-[#291B2D]">Intention:</span> {h.intention}</p>}
                  {h.win && <p className="text-sm text-[#59535B] mt-1"><span className="font-medium text-[#291B2D]">Win:</span> {h.win}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
